import {
  Avatar,
  Body1Strong,
  Caption1,
  Card,
  Link as FluentLink,
  Table,
  TableBody,
  TableCell,
  TableCellLayout,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';

import { DateLabel } from '@shared/components';
import { FileIcon, useItemTableStyles } from '@modules/items';

import { TTrainingRecord } from '../../types';
import TrainingStatusBadge from '../TrainingStatusBadge';

type TProps = {
  records: TTrainingRecord[];
  // "employee" lists documents for one employee; "document" lists employees for one document.
  subject: 'employee' | 'document';
  onSelectEmployee: (employeeId: string) => void;
  onSelectDocument: (documentId: string) => void;
  emptyMessage: string;
};

// Shared records table for both query directions. The name column links across to the
// other view, so a demo can hop from a document's gaps to that employee's record.
const TrainingRecordsTable = ({ records, subject, onSelectEmployee, onSelectDocument, emptyMessage }: TProps) => {
  const tableClasses = useItemTableStyles();

  if (records.length === 0) return <p>{emptyMessage}</p>;

  return (
    <Card className={tableClasses.card}>
      <Table className={tableClasses.table}>
        <TableHeader>
          <TableRow>
            <TableHeaderCell style={{ width: '280px' }}>
              <Body1Strong>{subject === 'employee' ? 'Document' : 'Employee'}</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '170px' }}>
              <Body1Strong>{subject === 'employee' ? 'Method' : 'Function'}</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '130px' }}>
              <Body1Strong>Trained revision</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '140px' }}>
              <Body1Strong>Status</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '120px' }}>
              <Body1Strong>Completed</Body1Strong>
            </TableHeaderCell>
            <TableHeaderCell style={{ width: '120px' }}>
              <Body1Strong>Due date</Body1Strong>
            </TableHeaderCell>
          </TableRow>
        </TableHeader>

        <TableBody className={tableClasses.tableBody}>
          {records.map((record) => (
            <TableRow key={`${record.employee.id}:${record.document.id}`}>
              <TableCell>
                {subject === 'employee' ? (
                  <TableCellLayout
                    media={<FileIcon type="DOCUMENT" path={[]} isBlock={false} isFrozen={false} isTemplate={false} />}
                    description={<Caption1 className="text-neutralFg-3">Current: {record.document.revision}</Caption1>}
                  >
                    <FluentLink appearance="subtle" onClick={() => onSelectDocument(record.document.id)}>
                      {record.document.name}
                    </FluentLink>
                  </TableCellLayout>
                ) : (
                  <TableCellLayout media={<Avatar name={record.employee.name} size={24} />}>
                    <FluentLink appearance="subtle" onClick={() => onSelectEmployee(record.employee.id)}>
                      {record.employee.name}
                    </FluentLink>
                  </TableCellLayout>
                )}
              </TableCell>
              <TableCell>
                {subject === 'employee' ? record.document.trainingMethod : record.employee.function}
              </TableCell>
              <TableCell>{record.revision ?? '-'}</TableCell>
              <TableCell>
                <TrainingStatusBadge status={record.status} />
              </TableCell>
              <TableCell>{record.completedAt ? <DateLabel short>{record.completedAt}</DateLabel> : '-'}</TableCell>
              <TableCell>{record.dueDate ? <DateLabel short>{record.dueDate}</DateLabel> : '-'}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
};

export default TrainingRecordsTable;
